import { makeStyles } from '@material-ui/core';
import { FC, useEffect, useState } from 'react';
import { Tag } from 'antd';
import Loader from 'src/component/Loader/Loader';
import { useProject } from 'src/reactQuery/projects/queries/useProject';
import { useUpdateProject } from 'src/reactQuery/projects/mutations/useUpdateProject';
import { useGetDynamicLinksQuery } from 'src/reactQuery/dynamicLinks/queries/useGetDynamicLinksQuery';
import { useCreateDynamicLinkMutation } from 'src/reactQuery/dynamicLinks/mutations/useCreateDynamicLinkMutation';
import { useUpdateDynamicLinkMutation } from 'src/reactQuery/dynamicLinks/mutations/useUpdateDynamicLinkMutation';
import { useDeleteDynamicLinkMutation } from 'src/reactQuery/dynamicLinks/mutations/useDeleteDynamicLinkMutation';
import {
  AndroidApp,
  AppleApp
} from 'src/services/napi/projects/interfaces/fetchProjects.interface';
import { DynamicLink } from 'src/services/napi/dynamicLinks/interfaces/dynamicLink.interface';
import { CreateDynamicLinkRequestBody } from 'src/services/napi/dynamicLinks/createDynamicLink/interfaces/createDynamicLink.interface';
import { UpdateDynamicLinkRequestBody } from 'src/services/napi/dynamicLinks/updateDynamicLink/interfaces/updateDynamicLink.interface';
import NoxTable from 'src/component/Table';
import AddOrEditDynamicLinkModal from './components/AddOrEditDynamicLinkModal';

const useStyle = makeStyles({
  pageTitle: {
    fontSize: '20px',
    fontWeight: 600,
    marginBottom: '20px',
    marginLeft: '35px',
    textAlign: 'start'
  },
  wrapperDiv: {
    backgroundColor: 'white',
    height: 'calc(100vh - 155px)',
    overflowY: 'scroll',
    paddingTop: '24px'
  }
});

const DynamicLinks: FC = () => {
  const classes = useStyle();

  const { data: projectData, refetch: refetchProject, isLoading: isProjectLoading } = useProject();

  const { mutate: updateProject } = useUpdateProject({
    onSuccess: () => refetchProject()
  });

  const {
    data: dynamicLinksData,
    refetch: refetchDynamicLinks,
    isLoading: isDynamicLinksLoading
  } = useGetDynamicLinksQuery({ onlyCreatedFromAdmin: true });

  const { mutate: createDynamicLink } = useCreateDynamicLinkMutation({
    onSuccess: () => refetchDynamicLinks()
  });

  const { mutate: updateDynamicLink } = useUpdateDynamicLinkMutation({
    onSuccess: () => refetchDynamicLinks()
  });

  const { mutate: deleteDynamicLink } = useDeleteDynamicLinkMutation({
    onSuccess: () => refetchDynamicLinks()
  });

  const [apps, setApps] = useState<{ androidApps: AndroidApp[]; appleApps: AppleApp[] }>({
    androidApps: [],
    appleApps: []
  });

  const [shortLinkPrefixes, setShortLinkPrefixes] = useState<string[]>([]);

  const [deepLinkPrefixes, setDeepLinkPrefixes] = useState<string[]>([]);

  const [fallbackUrlPrefixes, setFallbackUrlPrefixes] = useState<string[]>([]);

  const [dynamicLinks, setDynamicLinks] = useState<DynamicLink[]>([]);

  const [selectedDynamicLink, setSelectedDynamicLink] = useState<DynamicLink>();

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const handleCloseModal = () => {
    setSelectedDynamicLink(undefined);
    setIsModalOpen(false);
  };

  const handleUpdateApps = (updatedApps: { androidApps: AndroidApp[]; appleApps: AppleApp[] }) => {
    if (!projectData) {
      return;
    }

    updateProject({
      ...projectData,
      metadata: {
        ...(projectData.metadata ?? {}),
        dynamicLinks: {
          ...(projectData.metadata?.dynamicLinks ?? {}),
          ...updatedApps
        }
      }
    });
  };

  const handleUpdateShortLinkPrefixes = (shortLinkPrefixes: string[]) => {
    if (!projectData) {
      return;
    }

    updateProject({
      ...projectData,
      metadata: {
        ...(projectData.metadata ?? {}),
        dynamicLinks: {
          ...(projectData.metadata?.dynamicLinks ?? {}),
          shortLinkPrefixes
        }
      }
    });
  };

  const handleUpdateDeepLinkPrefixes = (deepLinkPrefixes: string[]) => {
    if (!projectData) {
      return;
    }

    updateProject({
      ...projectData,
      metadata: {
        ...(projectData.metadata ?? {}),
        dynamicLinks: {
          ...(projectData.metadata?.dynamicLinks ?? {}),
          deepLinkPrefixes
        }
      }
    });
  };

  const handleUpdateFallbackUrlPrefixes = (fallbackUrlPrefixes: string[]) => {
    if (!projectData) {
      return;
    }

    updateProject({
      ...projectData,
      metadata: {
        ...(projectData.metadata ?? {}),
        dynamicLinks: {
          ...(projectData.metadata?.dynamicLinks ?? {}),
          fallbackUrlPrefixes
        }
      }
    });
  };

  const handleCreateDynamicLink = (requestBody: CreateDynamicLinkRequestBody) => {
    createDynamicLink({ requestBody });
    handleCloseModal();
  };

  const handleUpdateDynamicLink = (
    dynamicLinkId: string,
    requestBody: UpdateDynamicLinkRequestBody
  ) => {
    updateDynamicLink({ dynamicLinkId, requestBody });
    handleCloseModal();
  };

  const handleDeleteDynamicLink = (dynamicLinkId: string) => {
    deleteDynamicLink({ dynamicLinkId });
    handleCloseModal();
  };

  useEffect(() => {
    if (!projectData) {
      return;
    }

    setApps({
      androidApps: projectData.metadata?.dynamicLinks?.androidApps ?? [],
      appleApps: projectData.metadata?.dynamicLinks?.appleApps ?? []
    });

    setShortLinkPrefixes(projectData.metadata?.dynamicLinks?.shortLinkPrefixes ?? []);
    setDeepLinkPrefixes(projectData.metadata?.dynamicLinks?.deepLinkPrefixes ?? []);
    setFallbackUrlPrefixes(projectData.metadata?.dynamicLinks?.fallbackUrlPrefixes ?? []);
  }, [projectData]);

  useEffect(() => {
    if (!dynamicLinksData) {
      return;
    }

    setDynamicLinks(dynamicLinksData);
  }, [dynamicLinksData]);

  if (isProjectLoading || isDynamicLinksLoading) {
    return <Loader />;
  }

  const columns = [
    {
      dataIndex: 'displayName',
      key: 'displayName',
      sorter: (a: DynamicLink, b: DynamicLink) => a.displayName?.localeCompare(b.displayName ?? ''),
      title: 'Link name'
    },
    {
      dataIndex: 'shortLink',
      key: 'shortLink',
      sorter: (a: DynamicLink, b: DynamicLink) => a.shortLink?.localeCompare(b.shortLink ?? ''),
      title: 'URL'
    },
    {
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: (a: DynamicLink, b: DynamicLink) =>
        a.createdAt &&
        b.createdAt &&
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      title: 'Created at'
    },
    {
      dataIndex: 'clicks',
      key: 'clicks',
      sorter: (a: DynamicLink, b: DynamicLink) => a.clicks && b.clicks && a.clicks - b.clicks,
      title: 'Clicks'
    },
    {
      key: 'edit',
      render: (data: DynamicLink) => (
        <Tag
          onClick={() => {
            setSelectedDynamicLink(data);
            setIsModalOpen(true);
          }}
        >
          Edit
        </Tag>
      )
    }
  ];

  const actions = [
    {
      onClick: () => setIsModalOpen(true),
      title: 'Add New Dynamic Link',
      type: 'primary'
    }
  ];

  return (
    <div className={classes.wrapperDiv}>
      <div className={classes.pageTitle}>Dynamic Links</div>

      <NoxTable
        actions={actions}
        checkbox={false}
        columns={columns}
        data={dynamicLinks}
        // @ts-ignore-next-line
        searchBy="displayName"
      />

      <AddOrEditDynamicLinkModal
        androidApps={apps.androidApps}
        appleApps={apps.appleApps}
        deepLinkPrefixes={deepLinkPrefixes}
        fallbackUrlPrefixes={fallbackUrlPrefixes}
        handleClose={handleCloseModal}
        handleCreateDynamicLink={handleCreateDynamicLink}
        handleDeleteDynamicLink={handleDeleteDynamicLink}
        handleUpdateApps={handleUpdateApps}
        handleUpdateDeepLinkPrefixes={handleUpdateDeepLinkPrefixes}
        handleUpdateDynamicLink={handleUpdateDynamicLink}
        handleUpdateFallbackUrlPrefixes={handleUpdateFallbackUrlPrefixes}
        handleUpdateShortLinkPrefixes={handleUpdateShortLinkPrefixes}
        initialDraft={selectedDynamicLink}
        isOpen={isModalOpen}
        shortLinkPrefixes={shortLinkPrefixes}
      />
    </div>
  );
};

export default DynamicLinks;
